import React from "react";
import {
  Box,
  Grid,
  Typography,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Chip,
  Card,
  CardContent,
  Paper,
  IconButton,
} from "@mui/material";
import { suggestCategory } from "../../../../../../data/ShoppingCategories";

/**
 * RecipePreviewDialog displays a recipe's details in a dialog
 * Supports multiple recipe formats:
 * - AI format: { n, d, mac, ing, i, ns }
 * - Firebase format: { name, description, macros, ingredients, instructions, servings }
 * - View mode format: { recipe: { ...firebase format }, servingsPerDay }
 */
const RecipePreviewDialog = ({ open, onClose, recipe }) => {
  if (!recipe) return null;

  // Handle nested recipe structure from view mode (recipe.recipe)
  const actualRecipe = recipe.recipe || recipe;

  // Handle both AI format and Firebase format
  const name = actualRecipe.n || actualRecipe.name;
  const description = actualRecipe.d || actualRecipe.description || "";
  const servings = actualRecipe.ns || actualRecipe.servings || recipe.servingsPerDay || 1;
  
  // Handle macros - could be in different formats
  let macros = { kCals: 0, p: 0, c: 0, f: 0, fi: 0 };
  if (actualRecipe.mac) {
    macros = {
      kCals: actualRecipe.mac.kCals || actualRecipe.mac.calories || 0,
      p: actualRecipe.mac.p || actualRecipe.mac.proteins || 0,
      c: actualRecipe.mac.c || actualRecipe.mac.carbs || 0,
      f: actualRecipe.mac.f || actualRecipe.mac.fats || 0,
      fi:
        actualRecipe.mac.fi ??
        actualRecipe.mac.fiber ??
        0,
    };
  } else if (actualRecipe.macros) {
    macros = {
      kCals: actualRecipe.macros.kCals || actualRecipe.macros.calories || 0,
      p: actualRecipe.macros.proteins || 0,
      c: actualRecipe.macros.carbs || 0,
      f: actualRecipe.macros.fats || 0,
      fi: actualRecipe.macros.fi ?? actualRecipe.macros.fiber ?? 0,
    };
  } else {
    // Try direct properties
    macros = {
      kCals: actualRecipe.kCals || actualRecipe.calories || 0,
      p: actualRecipe.proteins || 0,
      c: actualRecipe.carbs || 0,
      f: actualRecipe.fats || 0,
      fi: actualRecipe.fiber ?? 0,
    };
  }

  // Handle instructions - could be array of objects or string
  let instructions = "";
  if (Array.isArray(actualRecipe.i)) {
    instructions = actualRecipe.i
      .map((instruction, index) => {
        if (typeof instruction === "object" && instruction !== null) {
          const text =
            instruction.instruction || instruction.step || String(instruction);
          const duration = instruction.duration
            ? ` (${instruction.duration}s)`
            : "";
          return `${index + 1}. ${text}${duration}`;
        }
        return `${index + 1}. ${String(instruction)}`;
      })
      .join("\n");
  } else if (Array.isArray(actualRecipe.instructions)) {
    instructions = actualRecipe.instructions
      .map((instruction, index) => {
        if (typeof instruction === "object" && instruction !== null) {
          const text =
            instruction.instruction || instruction.step || String(instruction);
          const duration = instruction.duration
            ? ` (${instruction.duration}s)`
            : "";
          return `${index + 1}. ${text}${duration}`;
        }
        return `${index + 1}. ${String(instruction)}`;
      })
      .join("\n");
  } else if (actualRecipe.instructions) {
    instructions = String(actualRecipe.instructions);
  } else if (actualRecipe.i) {
    instructions = String(actualRecipe.i);
  }

  // Handle ingredients - could be in different formats
  let ingredients = [];
  if (Array.isArray(actualRecipe.ing)) {
    ingredients = actualRecipe.ing.map((ingredient) => ({
      name: ingredient.n || ingredient.name || "Unknown ingredient",
      amount: ingredient.q || ingredient.amount || 1,
      unit: ingredient.u || ingredient.unit || "g",
      category:
        ingredient.category ||
        suggestCategory(ingredient.n || ingredient.name || ""),
      proteins: ingredient.mac?.p || ingredient.proteins || 0,
      carbs: ingredient.mac?.c || ingredient.carbs || 0,
      fats: ingredient.mac?.f || ingredient.fats || 0,
      fiber: ingredient.mac?.fi ?? ingredient.fiber ?? 0,
    }));
  } else if (Array.isArray(actualRecipe.ingredients)) {
    ingredients = actualRecipe.ingredients.map((ingredient) => ({
      name: ingredient.name || "Unknown ingredient",
      amount: ingredient.amount || 1,
      unit: ingredient.unit || "g",
      category: ingredient.category || suggestCategory(ingredient.name || ""),
      proteins: ingredient.proteins || 0,
      carbs: ingredient.carbs || 0,
      fats: ingredient.fats || 0,
      fiber: ingredient.fiber || 0,
    }));
  }

  const perServingMacros = {
    kCals: Math.round(macros.kCals / servings),
    p: Math.round(macros.p / servings),
    c: Math.round(macros.c / servings),
    f: Math.round(macros.f / servings),
    fi: Math.round(macros.fi / servings),
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          bgcolor: "primary.main",
          color: "white",
        }}
      >
        <Box sx={{ fontWeight: 600, fontSize: "1.25rem" }}>{name}</Box>
        <IconButton edge="end" color="inherit" onClick={onClose} sx={{ ml: 1 }}>
          ×
        </IconButton>
      </DialogTitle>

      <DialogContent sx={{ p: 3 }}>
        {/* Macro Summary */}
        <Card sx={{ mb: 2, bgcolor: "grey.50" }}>
          <CardContent sx={{ p: 2 }}>
            <Typography
              variant="subtitle1"
              sx={{ fontWeight: 600, mb: 1, color: "primary.main" }}
            >
              Nutrition (per serving)
            </Typography>
            <Box sx={{ display: "flex", gap: 2, flexWrap: "wrap" }}>
              <Chip
                label={`${perServingMacros.kCals} cal`}
                color="primary"
                variant="filled"
                sx={{ fontWeight: 600 }}
              />
              <Chip
                label={`${perServingMacros.p}g Protein`}
                color="success"
                variant="outlined"
              />
              <Chip
                label={`${perServingMacros.c}g Carbs`}
                color="warning"
                variant="outlined"
              />
              <Chip
                label={`${perServingMacros.f}g Fats`}
                color="info"
                variant="outlined"
              />
              {perServingMacros.fi > 0 && (
                <Chip
                  label={`${perServingMacros.fi}g Fiber`}
                  color="success"
                  variant="outlined"
                />
              )}
              <Chip
                label={`${servings} serving${servings !== 1 ? "s" : ""}`}
                color="secondary"
                variant="outlined"
              />
              {actualRecipe.isExisting && (
                <Chip
                  label="From Recipe Library"
                  color="primary"
                  variant="outlined"
                  size="small"
                />
              )}
            </Box>
          </CardContent>
        </Card>

        {/* Description */}
        {description && (
          <Box sx={{ mb: 2 }}>
            <Typography
              variant="subtitle1"
              sx={{ fontWeight: 600, mb: 1, color: "primary.main" }}
            >
              Description
            </Typography>
            <Typography
              variant="body2"
              sx={{ color: "text.secondary", fontStyle: "italic" }}
            >
              {description}
            </Typography>
          </Box>
        )}

        {/* Ingredients */}
        {ingredients.length > 0 && (
          <Box sx={{ mb: 2 }}>
            <Typography
              variant="subtitle1"
              sx={{ fontWeight: 600, mb: 1, color: "primary.main" }}
            >
              Ingredients ({ingredients.length})
            </Typography>
            <Grid container spacing={1}>
              {ingredients.map((ingredient, index) => (
                <Grid item xs={12} sm={6} key={index}>
                  <Paper
                    sx={{
                      p: 1,
                      bgcolor: "grey.50",
                      border: "1px solid",
                      borderColor: "grey.200",
                    }}
                  >
                    <Typography variant="body2" sx={{ fontWeight: 500 }}>
                      {ingredient.amount} {ingredient.unit} {ingredient.name}
                    </Typography>
                    {(ingredient.proteins > 0 ||
                      ingredient.carbs > 0 ||
                      ingredient.fats > 0 ||
                      (ingredient.fiber || 0) > 0) && (
                      <Box sx={{ display: "flex", gap: 0.5, mt: 0.5 }}>
                        {ingredient.proteins > 0 && (
                          <Chip
                            label={`${ingredient.proteins.toFixed(1)}P`}
                            size="small"
                            variant="outlined"
                            sx={{ fontSize: "0.6rem", height: "16px" }}
                          />
                        )}
                        {ingredient.carbs > 0 && (
                          <Chip
                            label={`${ingredient.carbs.toFixed(1)}C`}
                            size="small"
                            variant="outlined"
                            sx={{ fontSize: "0.6rem", height: "16px" }}
                          />
                        )}
                        {ingredient.fats > 0 && (
                          <Chip
                            label={`${ingredient.fats.toFixed(1)}F`}
                            size="small"
                            variant="outlined"
                            sx={{ fontSize: "0.6rem", height: "16px" }}
                          />
                        )}
                        {(ingredient.fiber || 0) > 0 && (
                          <Chip
                            label={`${Number(ingredient.fiber).toFixed(1)}Fi`}
                            size="small"
                            variant="outlined"
                            sx={{ fontSize: "0.6rem", height: "16px" }}
                          />
                        )}
                      </Box>
                    )}
                  </Paper>
                </Grid>
              ))}
            </Grid>
          </Box>
        )}

        {/* Instructions */}
        {instructions && (
          <Box sx={{ mb: 2 }}>
            <Typography
              variant="subtitle1"
              sx={{ fontWeight: 600, mb: 1, color: "primary.main" }}
            >
              Instructions
            </Typography>
            <Paper sx={{ p: 2, bgcolor: "grey.50" }}>
              <Typography
                variant="body2"
                sx={{ whiteSpace: "pre-line", lineHeight: 1.6 }}
              >
                {instructions}
              </Typography>
            </Paper>
          </Box>
        )}

        {/* Total Recipe Macros */}
        <Card sx={{ bgcolor: "primary.light" }}>
          <CardContent sx={{ p: 2 }}>
            <Typography
              variant="subtitle1"
              sx={{ fontWeight: 600, mb: 1, color: "white" }}
            >
              Total Recipe Nutrition
            </Typography>
            <Box sx={{ display: "flex", gap: 2, flexWrap: "wrap" }}>
              <Chip
                label={`${macros.kCals} total calories`}
                sx={{
                  bgcolor: "white",
                  color: "primary.main",
                  fontWeight: 600,
                }}
              />
              <Chip
                label={`${macros.p}g total protein`}
                sx={{ bgcolor: "white", color: "primary.main" }}
              />
              <Chip
                label={`${macros.c}g total carbs`}
                sx={{ bgcolor: "white", color: "primary.main" }}
              />
              <Chip
                label={`${macros.f}g total fats`}
                sx={{ bgcolor: "white", color: "primary.main" }}
              />
              {macros.fi > 0 && (
                <Chip
                  label={`${macros.fi}g total fiber`}
                  sx={{ bgcolor: "white", color: "primary.main" }}
                />
              )}
            </Box>
          </CardContent>
        </Card>
      </DialogContent>

      <DialogActions sx={{ p: 2 }}>
        <Button onClick={onClose} variant="contained">
          Close
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default RecipePreviewDialog;


